'use client';

// ===================================================
// GAM Command Center — full categorised slash menu
// ===================================================
//
// The tall catalogue that opens on "/", back at the caret. All twenty-seven
// plugins, grouped by category, filtered live by whatever is typed after the
// slash — plus the favourites/recents strip along the top, so the four icons
// that used to be the whole surface are still one click away.
//
// Three things are deliberate, and each one is a bug that already happened:
//
// 1. It does NOT go through the suggestion plugin's ReactRenderer + tippy.
//    That popup tore itself down a beat after opening (26.07, three fixes
//    missed the cause). Visibility is owned by TiptapEditor and the only
//    closers are Escape, a pick, and a click outside.
//
// 2. It does NOT depend on the window-event bridge reaching another surface.
//    The Sidebar תוספים panel mirrors the same slash and is welcome to, but
//    the catalogue no longer needs it to be there: the bridge failed four
//    times (26.07 ×3, 20.09) and the menu Gal asked for must not ride on it.
//
// 3. Keyboard navigation is a capture-phase document listener, not the
//    extension's onKeyDown. The plugin's own key handler dies with the
//    teardown in (1), so arrows and Enter would fall through to ProseMirror
//    and move the caret instead of the selection.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Editor, Range } from '@tiptap/core';
import type { SlashCommandItem } from './types';
import type { SlashCategory } from './slash-command-filter';
import { iconForCommand } from './slash-command-icons';
import { pushRecent, quickBarTitles, readRecents } from './slash-recents';
import { EditorOverlay, EDITOR_OVERLAY_Z } from './editor-overlay';

/** Matches .gam-slash-menu's max-height in editor.css, plus the chrome. */
const MENU_MAX_H = 380;
const MENU_MIN_W = 260;
const CARET_GAP = 8;
const EDGE = 8;

export interface SlashFullMenuProps {
  editor: Editor;
  /** Already filtered by the extension against the live query. */
  categories: SlashCategory[];
  query: string;
  /** Caret box in viewport coordinates, as the extension reported it. */
  caret: { top: number; bottom: number; left: number } | null;
  range: Range | null;
  /** The editor's own text direction — decides which way the menu hangs. */
  direction?: 'rtl' | 'ltr';
  onDismiss: () => void;
}

export function SlashFullMenu({
  editor,
  categories,
  query,
  caret,
  range,
  direction = 'rtl',
  onDismiss,
}: SlashFullMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recents, setRecents] = useState<string[]>([]);

  // localStorage during render would desync SSR — read after mount only.
  useEffect(() => {
    setRecents(readRecents());
  }, []);

  const flat = useMemo(() => categories.flatMap((c) => c.items), [categories]);

  // The strip only makes sense on the bare slash. Once the user is typing,
  // the filtered catalogue below IS the answer and a fixed row of four
  // favourites on top of it is just noise.
  const favourites = useMemo(() => {
    if (query.trim()) return [];
    const byTitle = new Map(flat.map((i) => [i.title, i]));
    return quickBarTitles(recents)
      .map((t) => byTitle.get(t))
      .filter((i): i is SlashCommandItem => Boolean(i));
  }, [flat, recents, query]);

  const run = useCallback(
    (item: SlashCommandItem | undefined) => {
      if (!item) return;
      setRecents(pushRecent(item.title));
      // `range` covers the "/" and everything typed after it, so running the
      // command also removes the trigger text.
      if (range) item.command({ editor, range });
      onDismiss();
    },
    [editor, range, onDismiss]
  );

  // Selection resets whenever the filtered list changes under it, otherwise
  // Enter fires whatever happens to sit at the old index.
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    const el = menuRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, categories]);

  // Capture phase, on document: ProseMirror must not see these first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss();
        return;
      }
      if (flat.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((p) => (p + 1) % flat.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((p) => (p + flat.length - 1) % flat.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        run(flat[selectedIndex]);
      }
    };

    // Any mousedown that isn't on the menu closes it — including inside the
    // editor, because the menu no longer closes when the extension exits, so
    // a deliberate click elsewhere is the clear dismissal. Typing never
    // closes it; it filters. The Sidebar's תוספים panel mirrors this same
    // slash and is exempt, matched by attribute because this package cannot
    // import app-level components.
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-gam-slash-menu-mirror]')) return;
      onDismiss();
    };

    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [flat, selectedIndex, run, onDismiss]);

  if (!caret) return null;

  // Below the caret by default; flipped above when the viewport is short.
  // Both branches are anchored to the caret, never to the menu's own height,
  // so the position does not jump as the filtered list shrinks.
  const viewportH = typeof window === 'undefined' ? 900 : window.innerHeight;
  const viewportW = typeof window === 'undefined' ? 1200 : window.innerWidth;
  const openDown = caret.bottom + CARET_GAP + MENU_MAX_H <= viewportH;

  // Hangs toward the leading edge, so it reads as coming out of the caret
  // instead of running off the trailing side of the window. Anchored with
  // `right` in RTL rather than a computed left, because the menu's real width
  // is content-driven and a guess at it would drift as the list filters.
  const horizontal =
    direction === 'rtl'
      ? {
          right: Math.min(
            Math.max(EDGE, viewportW - caret.left - 24),
            Math.max(EDGE, viewportW - MENU_MIN_W - EDGE)
          ),
        }
      : {
          left: Math.min(
            Math.max(EDGE, caret.left - 24),
            Math.max(EDGE, viewportW - MENU_MIN_W - EDGE)
          ),
        };

  const vertical = openDown
    ? { top: caret.bottom + CARET_GAP }
    : { bottom: Math.max(EDGE, viewportH - caret.top + CARET_GAP) };

  let globalIndex = 0;

  return (
    <EditorOverlay>
      <div
        ref={menuRef}
        className="gam-slash-menu"
        dir="rtl"
        role="listbox"
        aria-label="תפריט פקודות"
        style={{ position: 'fixed', zIndex: EDITOR_OVERLAY_Z, ...horizontal, ...vertical }}
      >
        {favourites.length > 0 && (
          <div className="gam-slash-menu__quick">
            {favourites.map((item) => {
              const Icon = iconForCommand(item.title);
              return (
                <button
                  key={`quick-${item.title}`}
                  type="button"
                  className="gam-slash-menu__quick-btn"
                  onClick={() => run(item)}
                  aria-label={item.titleHe}
                  title={item.titleHe}
                >
                  <Icon className="gam-slash-menu__quick-icon" />
                </button>
              );
            })}
          </div>
        )}

        {flat.length === 0 && <div className="gam-slash-menu__empty">אין תוצאות</div>}

        {categories.map((cat) => (
          <div key={cat.name} className="gam-slash-menu__category">
            <div className="gam-slash-menu__header">{cat.nameHe}</div>
            {cat.items.map((item) => {
              const idx = globalIndex++;
              return (
                <button
                  key={item.title}
                  type="button"
                  data-index={idx}
                  role="option"
                  aria-selected={idx === selectedIndex}
                  className={`gam-slash-menu__item ${
                    idx === selectedIndex ? 'gam-slash-menu__item--active' : ''
                  }`}
                  onClick={() => run(item)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <span className="gam-slash-menu__icon">{item.icon}</span>
                  <div className="gam-slash-menu__text">
                    <span className="gam-slash-menu__title">{item.titleHe}</span>
                    <span className="gam-slash-menu__desc">{item.description}</span>
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </EditorOverlay>
  );
}
